import { FormEvent, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Input } from "antd";
import { Send } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { Seo } from "@/components/seo/Seo";
import { appConfig } from "@/config/app.config";
import { markPremiumWaitlistJoined } from "@/lib/waitlist";
import { routePaths } from "@/routes/routePaths";
import { joinPremiumWaitlist } from "@/services/api";

export function RegisterPage() {
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const waitlistMutation = useMutation({
    mutationFn: joinPremiumWaitlist,
    onSuccess: () => {
      markPremiumWaitlistJoined();
    }
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    waitlistMutation.mutate({
      name,
      email,
      sourcePath: location.pathname
    });
  }

  const isSubmitted = waitlistMutation.isSuccess;

  return (
    <>
      <Seo
        path={routePaths.register}
        title={`Premium Tier Waitlist | ${appConfig.name}`}
        description="Join the premium tier waitlist for TikTok/Reels link transcription, ad-free tools, and higher file limits."
        robots="noindex,follow"
      />
      <section className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="max-w-xl">
          <p className="text-sm font-medium uppercase tracking-[0.12em] text-neutral-500">
            Premium access
          </p>
          <h1 className="mt-3 text-4xl font-semibold tracking-normal sm:text-5xl">
            Premium tier waitlist
          </h1>
          <p className="mt-4 text-base leading-7 text-neutral-600 dark:text-neutral-400">
            Get notified when TikTok/Reels link transcription, ad-free tools,
            and higher file limits open up.
          </p>
        </div>

        <form
          className="w-full border border-neutral-200 p-4 dark:border-neutral-800 sm:p-6"
          onSubmit={handleSubmit}
        >
          <div className="grid gap-4">
            <label className="grid gap-2 text-sm font-medium">
              Name
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Jane Doe"
                autoComplete="name"
                size="large"
                disabled={isSubmitted}
                required
              />
            </label>
            <label className="grid gap-2 text-sm font-medium">
              Email
              <Input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="jane@example.com"
                autoComplete="email"
                inputMode="email"
                size="large"
                disabled={isSubmitted}
                required
              />
            </label>

            {waitlistMutation.error ? (
              <Alert type="error" showIcon message={waitlistMutation.error.message} className="rounded-none" />
            ) : null}

            {isSubmitted ? (
              <Alert
                type="success"
                showIcon
                message="You're on the premium tier waitlist."
                description="We'll use this email only for premium tier launch updates."
                className="rounded-none"
              />
            ) : null}

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              loading={waitlistMutation.isPending}
              disabled={isSubmitted}
              icon={<Send className="h-4 w-4" />}
              className="h-11 rounded-none bg-neutral-950 font-medium dark:bg-white dark:text-neutral-950"
            >
              Join waitlist
            </Button>

            <p className="text-xs leading-5 text-neutral-500 dark:text-neutral-400">
              By joining, you agree that {appConfig.name} can email you about the premium tier. Read the{" "}
              <Link to={routePaths.privacy} className="underline-offset-4 hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </form>
      </section>
    </>
  );
}
